import { useMemo } from "react";
import { TicketLabel, TicketSource, TicketInternalStatus } from "shared/types/helpdesk";
import { UniversalPickerMode, PickerConfig } from "../types";
import { SECONDARY_FILE_LABELS, STATUS_OPTIONS } from "../constants/pickerConstants";
import { useActiveUsers } from "shared/contexts/users.context";
import { useBuildingsAndUnits } from "hooks/useBuildingsAndUnits";
import { BugPriority, BugStatus, ItemType } from "shared/types/bug-report";

export const usePickerSearch = (
  mode: UniversalPickerMode,
  searchTerm: string,
  config: PickerConfig,
  suppliers: any[],
  contacts: any[],
  assets: any[] = [],
  apiData: any[] = [],
  epics: any[] = [],
  renovationProjects: any[] = [],
  projectParts: any[] = [],
  resources: any[] = []
) => {
  const { users: activeUsers } = useActiveUsers();
  const { buildings, units, getUnitsForBuilding } = useBuildingsAndUnits();

  const filteredData = useMemo(() => {
    switch (mode) {
      case "helpdesk-label":
        return Object.values(TicketLabel)
          .filter(label => {
            if (!searchTerm.trim()) return true;
            return label.toLowerCase().includes(searchTerm.toLowerCase());
          })
          .sort((a, b) => a.localeCompare(b));

      case "helpdesk-status":
        return Object.values(TicketInternalStatus)
          .filter(status => {
            if (!searchTerm.trim()) return true;
            return status.toLowerCase().includes(searchTerm.toLowerCase());
          });

      case "file-label":
        return config.databaseFileLabels?.filter(label => {
          const matchesSearch = label.label.toLowerCase().includes(searchTerm.toLowerCase());
          if (!matchesSearch) return false;

          if (config.currentUnitId) {
            return label.resourceType === 'unit' || label.resourceType === 'building';
          }
          if (config.currentBuildingId) {
            return label.resourceType === 'building';
          }
          return true;
        }).sort((a, b) => {
          if (config.currentUnitId) {
            if (a.resourceType === 'unit' && b.resourceType !== 'unit') return -1;
            if (a.resourceType !== 'unit' && b.resourceType === 'unit') return 1;
            if (a.resourceType === 'building' && b.resourceType !== 'building') return -1;
            if (a.resourceType !== 'building' && b.resourceType === 'building') return 1;
          } else if (config.currentBuildingId) {
            if (a.resourceType === 'building' && b.resourceType !== 'building') return -1;
            if (a.resourceType !== 'building' && b.resourceType === 'building') return 1;
          }
          return a.label.localeCompare(b.label);
        }) || [];

      case "secondary-file-label":
        return SECONDARY_FILE_LABELS
          .filter(label => label.toLowerCase().includes(searchTerm.toLowerCase()));

      case "assignee":
        return activeUsers?.filter(user => {
          if (!searchTerm.trim()) return true;
          const query = searchTerm.toLowerCase();
          return user.first_name?.toLowerCase().includes(query) ||
                 user.last_name?.toLowerCase().includes(query) ||
                 `${user.first_name} ${user.last_name}`.toLowerCase().includes(query);
        }) || [];

      case "building":
        return buildings.filter(building =>
          building.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          building.company?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

      case "unit":
        const availableUnits = config.currentBuildingId
          ? getUnitsForBuilding(config.currentBuildingId)
          : units;
        return availableUnits.filter(unit =>
          unit.id.toLowerCase().includes(searchTerm.toLowerCase())
        );

      case "epic":
        return epics.filter(epic => {
          if (!searchTerm.trim()) return true;
          const query = searchTerm.toLowerCase();
          return epic.title?.toLowerCase().includes(query) ||
                 epic.description?.toLowerCase().includes(query) ||
                 epic.id?.toString().includes(query);
        });

      case "renovation-project":
        // Search is handled by API, just return the data
        return renovationProjects;

      case "project-part":
        // Filter project parts by search term if provided
        return projectParts.filter(part => {
          if (!searchTerm.trim()) return true;
          const query = searchTerm.toLowerCase();
          return part.title?.toLowerCase().includes(query);
        });

      case "supplier":
        return suppliers.filter(supplier => {
          if (!searchTerm.trim()) return true;
          const query = searchTerm.toLowerCase();
          return supplier.name?.toLowerCase().includes(query) ||
                 supplier.email?.toLowerCase().includes(query) ||
                 supplier.tags?.some((tag: string) => tag.toLowerCase().includes(query));
        });

      case "reporter":
        return contacts.filter(contact => {
          // Exclude contact by ID if specified (e.g., when merging contacts)
          if (config.excludeId && contact.id?.toString() === config.excludeId?.toString()) {
            return false;
          }

          const query = searchTerm.toLowerCase();
          // Create full name for combined search
          const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase().trim();

          return contact.firstName?.toLowerCase().includes(query) ||
                 contact.lastName?.toLowerCase().includes(query) ||
                 contact.email?.toLowerCase().includes(query) ||
                 fullName.includes(query);
        });

      case "asset":
        return assets.filter(asset => {
          if (!searchTerm.trim()) return true;
          const query = searchTerm.toLowerCase();
          return asset.name?.toLowerCase().includes(query) ||
                 asset.assetCategory?.name?.toLowerCase().includes(query) ||
                 asset.building?.resourceId?.toLowerCase().includes(query) ||
                 asset.unit?.resourceId?.toLowerCase().includes(query);
        });

      case "source":
        return Object.values(TicketSource).filter(source => {
          if (!searchTerm.trim()) return true;
          return source.toLowerCase().includes(searchTerm.toLowerCase());
        });

      case "status":
        return STATUS_OPTIONS;

      case "custom-tags":
        // Use customOptions from config and filter by search term
        return (config.customOptions || [])
          .filter(option => {
            if (!searchTerm.trim()) return true;
            const optionText = typeof option === 'string' ? option : option.label;
            return optionText.toLowerCase().includes(searchTerm.toLowerCase());
          });

      case "rental-contract":
        // For API-driven modes, use the apiData directly
        // Additional filtering can be done here if needed
        return apiData.filter(item => {
          if (!config.apiConfig || !searchTerm.trim()) return true;

          const displayField = config.apiConfig.displayField || 'name';
          const secondaryField = config.apiConfig.secondaryField;

          const query = searchTerm.toLowerCase();

          // Search in primary display field
          if (item[displayField]?.toLowerCase().includes(query)) return true;

          // Search in secondary field if specified
          if (secondaryField && item[secondaryField]?.toLowerCase().includes(query)) return true;

          return false;
        });

      case "bug-priority":
        return Object.values(BugPriority).filter(priority => {
          if (!searchTerm.trim()) return true;
          return priority.toLowerCase().includes(searchTerm.toLowerCase());
        });

      case "bug-type":
        return Object.values(ItemType).filter(type => {
          if (!searchTerm.trim()) return true;
          return type.toLowerCase().includes(searchTerm.toLowerCase());
        });

      case "bug-status":
        return Object.values(BugStatus).filter(status => {
          if (!searchTerm.trim()) return true;
          return status.toLowerCase().includes(searchTerm.toLowerCase());
        });

      case "acquisition-file":
        // For API-driven modes, use the apiData directly with filtering
        return apiData.filter(item => {
          if (!config.apiConfig || !searchTerm.trim()) return true;

          const displayField = config.apiConfig.displayField || 'name';
          const secondaryField = config.apiConfig.secondaryField || 'id';

          const query = searchTerm.toLowerCase();

          // Search in primary display field
          if (item[displayField]?.toString().toLowerCase().includes(query)) return true;

          // Search in secondary field if specified (including id)
          if (secondaryField && item[secondaryField]?.toString().toLowerCase().includes(query)) return true;

          return false;
        });

      case "resource":
        // Resources are already filtered by the API search, just return them
        return resources;

      case "room":
        // API-driven: filter rooms by search term
        return apiData.filter(item => {
          if (!searchTerm.trim()) return true;
          const query = searchTerm.toLowerCase();
          const roomType = item.roomType || '';
          return roomType.toLowerCase().includes(query) ||
                 item.id?.toString().includes(query);
        });

      default:
        return [];
    }
  }, [mode, searchTerm, config, activeUsers, buildings, units, getUnitsForBuilding, suppliers, contacts, assets, apiData, epics, resources]);

  return filteredData;
};